import React, { useState, useEffect, useCallback, useMemo } from "react";
import { GoTrash } from "react-icons/go";
import { IoMdAdd } from "react-icons/io";
import { 
  FiEdit2, 
  FiSearch, 
  FiCalendar, 
  FiUser, 
  FiGlobe, 
  FiEye, 
  FiRefreshCw,
  FiGrid,
  FiList,
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiBookOpen,
  FiCopy,
  FiCheck,
  FiX
} from "react-icons/fi";
import { toast } from "react-toastify";
import ConfirmationModal from "../../../components/ui/ConfirmationModal";
import PageHeader from "../../../components/ui/PageHeader";
import Button from "../../../components/ui/Button";
import Pagination from "../../../components/ui/Pagination";
import { strings } from "../../../strings/strings";
import BlogModal from "./BlogModal";
import BlogPreviewModal from "./BlogPreviewModal";
import DOMPurify from "dompurify";
import { INITIAL_BLOGS } from "./blogMockData";
import { resolveImageUrl } from "./seoHelpers";
import {
  getBlogListApi,
  getBlogDetailsApi,
  createBlogApi,
  updateBlogApi,
  deleteBlogApi,
  toggleBlogStatusApi,
} from "../../../api/blogApi";

const STORAGE_KEY = "peoplesync_hrms_blogs_v2";

const BlogsPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Filter & Search states
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    blog: null,
    loading: false,
  });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, blog: null });
  const [viewMode, setViewMode] = useState("grid"); // "grid" | "list"
  const [copiedSlug, setCopiedSlug] = useState(null);

  const handleCopySlug = (slug, e) => {
    e.stopPropagation();
    if (!slug) return;
    const url = `${window.location.origin}/blog/${slug}`;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(url);
    }
    setCopiedSlug(slug);
    toast.success("Blog link copied to clipboard!");
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  const blogStats = useMemo(() => {
    const total = totalItems || blogs.length;
    const published = blogs.filter((b) => (b.status || "Published") === "Published").length;
    const drafts = blogs.filter((b) => b.status === "Draft").length;
    const scheduled = blogs.filter((b) => b.status === "Scheduled").length;
    const totalViews = blogs.reduce((sum, b) => sum + (Number(b.views) || 0), 0);
    return { total, published, drafts, scheduled, totalViews };
  }, [blogs, totalItems]);

  // 1. Fetch Blog List from backend API
  const fetchBlogs = useCallback(async (isManualRefresh = false) => {
    if (isManualRefresh) setIsRefreshing(true);
    else setIsLoading(true);

    try {
      const response = await getBlogListApi({
        page: currentPage,
        limit: itemsPerPage,
        search: searchQuery,
        status: statusFilter,
      });

      const rawResults = response?.results || response?.data || response;
      const blogList = Array.isArray(rawResults)
        ? rawResults
        : (rawResults?.data || (Array.isArray(rawResults?.blogs) ? rawResults.blogs : []));
      const pagination = rawResults?.pagination || response?.pagination;

      if (Array.isArray(blogList) && blogList.length > 0) {
        setBlogs(blogList);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(blogList));

        if (pagination) {
          setTotalPages(pagination.totalPages || 1);
          setTotalItems(pagination.totalItems ?? blogList.length);
        } else {
          setTotalPages(Math.ceil(blogList.length / itemsPerPage) || 1);
          setTotalItems(blogList.length);
        }
      } else if (response?.success) {
        setBlogs([]);
        setTotalPages(1);
        setTotalItems(0);
      } else {
        throw new Error("No API response data");
      }
    } catch {
      // Graceful offline fallback
      const localData = localStorage.getItem(STORAGE_KEY);
      let fallbackList = INITIAL_BLOGS;
      if (localData) {
        try {
          fallbackList = JSON.parse(localData);
        } catch {
          fallbackList = INITIAL_BLOGS;
        }
      }
      setBlogs(fallbackList);
      setTotalPages(Math.ceil(fallbackList.length / itemsPerPage) || 1);
      setTotalItems(fallbackList.length);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [currentPage, itemsPerPage, searchQuery, statusFilter]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  // Reset to page 1 on filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  // 2. Add / Edit / View Handlers
  const handleAdd = () => {
    setSelectedBlog(null);
    setIsModalOpen(true);
  };

  const handleEdit = async (blog) => {
    const blogId = blog?._id || blog?.id;
    if (!blogId) {
      setSelectedBlog(blog);
      setIsModalOpen(true);
      return;
    }

    try {
      const response = await getBlogDetailsApi(blogId);
      const blogDetails =
        response?.results?.blog || response?.results || response?.data || response;
      setSelectedBlog(blogDetails && typeof blogDetails === "object" ? blogDetails : blog);
    } catch {
      setSelectedBlog(blog);
    } finally {
      setIsModalOpen(true);
    }
  };

  const handleView = async (blog) => {
    const blogId = blog?._id || blog?.id;
    setPreviewModal({
      isOpen: true,
      blog: blog,
      loading: Boolean(blogId),
    });

    if (blogId) {
      try {
        const response = await getBlogDetailsApi(blogId);
        const blogDetails =
          response?.results?.blog || response?.results || response?.data || response;
        if (blogDetails && typeof blogDetails === "object") {
          setPreviewModal({
            isOpen: true,
            blog: blogDetails,
            loading: false,
          });
        } else {
          setPreviewModal((prev) => ({ ...prev, loading: false }));
        }
      } catch {
        setPreviewModal((prev) => ({ ...prev, loading: false }));
      }
    }
  };

  const handleSave = async (blogPayload) => {
    const isEdit = Boolean(selectedBlog);
    const blogId = selectedBlog?._id || selectedBlog?.id;

    try {
      if (isEdit) {
        await updateBlogApi(blogId, blogPayload);
      } else {
        await createBlogApi(blogPayload);
      }

      setIsModalOpen(false);
      setSelectedBlog(null);
      fetchBlogs();
    } catch {
      // Errors (400, 404, 409) are toasted automatically by apiCall
    }
  };

  // 3. Delete Handler
  const handleDelete = (blog) => {
    setConfirmModal({ isOpen: true, blog });
  };

  const confirmDelete = async () => {
    const blogToDelete = confirmModal.blog;
    if (!blogToDelete) return;

    const blogId = blogToDelete._id || blogToDelete.id;
    try {
      await deleteBlogApi(blogId);
      fetchBlogs();
    } catch {
      // Errors are toasted automatically
    } finally {
      setConfirmModal({ isOpen: false, blog: null });
    }
  };

  // 4. Status Toggle Handler
  const handleToggleStatus = async (blog) => {
    const blogId = blog._id || blog.id;
    const nextStatus = blog.status === "Published" ? "Draft" : "Published";

    try {
      await toggleBlogStatusApi(blogId, nextStatus);
      fetchBlogs();
    } catch {
      // Errors are toasted automatically
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    try {
      return new Date(isoString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return isoString;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "Published":
        return "bg-emerald-950/70 text-emerald-300 border-emerald-500/30";
      case "Scheduled":
        return "bg-blue-950/70 text-blue-300 border-blue-500/30";
      default:
        return "bg-amber-950/70 text-amber-300 border-amber-500/30";
    }
  };

  const getStatusDotClass = (status) => {
    switch (status) {
      case "Published":
        return "bg-emerald-400 animate-pulse";
      case "Scheduled":
        return "bg-blue-400 animate-pulse";
      default:
        return "bg-amber-400";
    }
  };

  const renderBlogCardContent = (blog) => {
    const rawContent = blog.metaDescription || blog.excerpt || blog.content || "";
    if (!rawContent) {
      return (
        <p className="text-xs sm:text-sm text-gray-400 dark:text-gray-500 italic line-clamp-2 mb-3">
          No excerpt provided for this article.
        </p>
      );
    }

    const cleanHtml = DOMPurify.sanitize(rawContent);

    return (
      <div
        className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3 leading-relaxed break-words [&_p]:inline [&_h1]:inline [&_h2]:inline [&_h3]:inline [&_strong]:font-semibold [&_em]:italic [&_a]:text-primary"
        dangerouslySetInnerHTML={{ __html: cleanHtml }}
      />
    );
  };

  return (
    <div className="min-h-screen pb-12">
      <PageHeader
        title={strings.BLOG}
        description={strings.BLOG_DESCRIPTION}
        icon={FiBookOpen}
        badge={`${totalItems} ${totalItems === 1 ? "Article" : "Articles"}`}
      >
        <Button onClick={handleAdd} className="text-xs sm:text-sm flex items-center shadow-sm h-10 px-4">
          <IoMdAdd className="w-4 h-4 mr-1.5" />
          {strings.ADD_BLOG}
        </Button>
      </PageHeader>

      {/* 2. Interactive KPI Stats Bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {/* Total Articles */}
        <div
          onClick={() => setStatusFilter("All")}
          className={`group bg-white dark:bg-slate-900 rounded-xl p-4 sm:p-5 border transition-all duration-200 cursor-pointer hover:shadow-md hover:-translate-y-0.5 relative overflow-hidden ${
            statusFilter === "All"
              ? "border-primary shadow-xs ring-1 ring-primary/30"
              : "border-gray-200/80 dark:border-slate-800 shadow-xs hover:border-gray-300 dark:hover:border-slate-700"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Total Posts
              </p>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {blogStats.total}
              </h3>
            </div>
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
              <FiFileText className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3 flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
            <span>All categories included</span>
          </div>
        </div>

        {/* Published */}
        <div
          onClick={() => setStatusFilter("Published")}
          className={`group bg-white dark:bg-slate-900 rounded-xl p-4 sm:p-5 border transition-all duration-200 cursor-pointer hover:shadow-md hover:-translate-y-0.5 relative overflow-hidden ${
            statusFilter === "Published"
              ? "border-primary shadow-xs ring-1 ring-primary/30"
              : "border-gray-200/80 dark:border-slate-800 shadow-xs hover:border-gray-300 dark:hover:border-slate-700"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Published
              </p>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {blogStats.published}
              </h3>
            </div>
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
              <FiCheckCircle className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3 flex items-center gap-1.5 text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span>Live on public site</span>
          </div>
        </div>

        {/* Drafts */}
        <div
          onClick={() => setStatusFilter("Draft")}
          className={`group bg-white dark:bg-slate-900 rounded-xl p-4 sm:p-5 border transition-all duration-200 cursor-pointer hover:shadow-md hover:-translate-y-0.5 relative overflow-hidden ${
            statusFilter === "Draft"
              ? "border-primary shadow-xs ring-1 ring-primary/30"
              : "border-gray-200/80 dark:border-slate-800 shadow-xs hover:border-gray-300 dark:hover:border-slate-700"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Drafts
              </p>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {blogStats.drafts}
              </h3>
            </div>
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
              <FiEdit2 className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3 flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
            <span>Pending review / editing</span>
          </div>
        </div>

        {/* Scheduled */}
        <div
          onClick={() => setStatusFilter("Scheduled")}
          className={`group bg-white dark:bg-slate-900 rounded-xl p-4 sm:p-5 border transition-all duration-200 cursor-pointer hover:shadow-md hover:-translate-y-0.5 relative overflow-hidden ${
            statusFilter === "Scheduled"
              ? "border-primary shadow-xs ring-1 ring-primary/30"
              : "border-gray-200/80 dark:border-slate-800 shadow-xs hover:border-gray-300 dark:hover:border-slate-700"
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Scheduled
              </p>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {blogStats.scheduled}
              </h3>
            </div>
            <div className="w-10 h-10 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
              <FiClock className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3 flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
            <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
            <span>Upcoming scheduled releases</span>
          </div>
        </div>
      </div>

      {/* 3. Search & Filter Bar with View Mode Switcher */}
      <div className="bg-white my-6 dark:bg-slate-900 rounded-xl p-3 sm:p-4 border border-gray-200/80 dark:border-slate-800 shadow-xs flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
        {/* Search Input */}
        <div className="relative flex-1 max-w-md">
          <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder={strings.SEARCH_BLOGS}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-10 pl-10 pr-9 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary/40 focus:border-primary dark:bg-slate-800 dark:text-white outline-none text-xs sm:text-sm transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-0.5"
            >
              <FiX className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Filters & View Modes */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Status Quick Pills */}
          <div className="inline-flex h-10 items-center rounded-xl p-1 bg-gray-100 dark:bg-slate-800 text-xs font-medium">
            {["All", "Published", "Draft", "Scheduled"].map((st) => (
              <button
                key={st}
                type="button"
                onClick={() => setStatusFilter(st)}
                className={`h-8 px-3 rounded-lg text-xs transition-all flex items-center justify-center ${
                  statusFilter === st
                    ? "bg-white dark:bg-slate-900 text-gray-900 dark:text-white font-semibold shadow-xs"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                {st}
              </button>
            ))}
          </div>

          {/* View Mode Switcher (Grid vs List) */}
          <div className="inline-flex h-10 items-center rounded-xl p-1 bg-gray-100 dark:bg-slate-800">
            <button
              type="button"
              onClick={() => setViewMode("grid")}
              title="Grid View"
              className={`h-8 w-8 rounded-lg flex items-center justify-center transition-all ${
                viewMode === "grid"
                  ? "bg-white dark:bg-slate-900 text-primary shadow-xs"
                  : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              }`}
            >
              <FiGrid className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode("list")}
              title="List View"
              className={`h-8 w-8 rounded-lg flex items-center justify-center transition-all ${
                viewMode === "list"
                  ? "bg-white dark:bg-slate-900 text-primary shadow-xs"
                  : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              }`}
            >
              <FiList className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* 4. Blog Cards List / Grid */}
      {isLoading ? (
        <div className="grid gap-6 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200/80 dark:border-slate-800 p-5 shadow-xs animate-pulse flex flex-col justify-between"
            >
              <div>
                <div className="w-full aspect-[16/9] rounded-xl bg-gray-200 dark:bg-slate-800 mb-4" />
                <div className="h-5 bg-gray-200 dark:bg-slate-800 rounded-md w-3/4 mb-2.5" />
                <div className="h-4 bg-gray-100 dark:bg-slate-800/60 rounded-md w-1/2 mb-3" />
                <div className="h-3.5 bg-gray-100 dark:bg-slate-800/60 rounded-md w-full mb-1.5" />
                <div className="h-3.5 bg-gray-100 dark:bg-slate-800/60 rounded-md w-4/5 mb-4" />
              </div>
              <div className="pt-3 border-t border-gray-100 dark:border-slate-800 flex items-center justify-between">
                <div className="h-6 w-24 bg-gray-200 dark:bg-slate-800 rounded-full" />
                <div className="h-7 w-20 bg-gray-100 dark:bg-slate-800 rounded-lg" />
              </div>
            </div>
          ))}
        </div>
      ) : blogs.length > 0 ? (
        <>
          {viewMode === "grid" ? (
            <div className="grid gap-6 grid-cols-1 md:grid-cols-2 xl:grid-cols-3 mb-6">
              {blogs.map((blog) => {
                const blogId = blog._id || blog.id;
                const rawCover = blog.coverImage || blog.image;
                const coverImage = resolveImageUrl(rawCover);
                const authorName =
                  typeof blog.author === "string"
                    ? blog.author.trim()
                    : (blog.author?.name ? blog.author.name.trim() : "");

                const keywordsList = Array.isArray(blog.keywords)
                  ? blog.keywords
                  : (typeof blog.keywords === "string" && blog.keywords
                    ? blog.keywords.split(",").map((k) => k.trim()).filter(Boolean)
                    : (Array.isArray(blog.tags) ? blog.tags : []));

                return (
                  <div
                    key={blogId}
                    className="group bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-gray-200/80 dark:border-slate-800 p-5 flex flex-col justify-between hover:shadow-xl hover:border-primary/40 dark:hover:border-primary/40 transition-all duration-300"
                  >
                    <div>
                      {/* Cover Banner */}
                      <div className="relative aspect-[16/9] w-full rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800 mb-4 border border-gray-100 dark:border-slate-800/60 group">
                        {coverImage ? (
                          <img
                            src={coverImage}
                            alt={blog.title}
                            className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=800&auto=format&fit=crop&q=80";
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center relative p-6 text-center bg-gradient-to-br from-teal-500/10 via-primary/10 to-indigo-500/15 dark:from-teal-900/20 dark:via-primary/20 dark:to-indigo-900/30 overflow-hidden">
                            <div className="w-11 h-11 rounded-2xl bg-white/80 dark:bg-slate-800/80 backdrop-blur-md shadow-xs border border-white/40 dark:border-slate-700/50 flex items-center justify-center text-primary mb-1.5 group-hover:scale-110 transition-transform duration-300">
                              <FiBookOpen className="w-5 h-5" />
                            </div>
                            {blog.category && (
                              <span className="text-[11px] font-bold text-gray-700 dark:text-gray-300 tracking-wider uppercase">
                                {blog.category}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Top Badges Overlay */}
                        <div className="absolute top-2.5 left-2.5 right-2.5 flex items-center justify-between gap-1 pointer-events-none">
                          {blog.category && (
                            <span className="pointer-events-auto px-2.5 py-1 rounded-full text-[11px] font-semibold bg-black/50 text-white backdrop-blur-md border border-white/10 shadow-xs">
                              {blog.category}
                            </span>
                          )}

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleStatus(blog);
                            }}
                            className={`pointer-events-auto px-2.5 py-1 rounded-full text-[11px] font-semibold backdrop-blur-md shadow-xs border transition-transform duration-200 flex items-center gap-1.5 hover:scale-105 ${getStatusBadgeClass(blog.status)}`}
                            title="Click to toggle status"
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${getStatusDotClass(blog.status)}`} />
                            <span>{blog.status || "Published"}</span>
                          </button>
                        </div>
                      </div>

                      {/* Slug with Quick Copy */}
                      {blog.slug && (
                        <div className="flex items-center justify-between gap-1.5 text-xs text-slate-500 dark:text-slate-400 font-mono bg-slate-50 dark:bg-slate-800/80 px-2.5 py-1 rounded-lg border border-slate-200/70 dark:border-slate-700/70 mb-2.5 group/slug hover:border-primary/40 transition-colors">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <FiGlobe className="w-3.5 h-3.5 text-primary shrink-0" />
                            <span className="truncate">/blog/{blog.slug}</span>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => handleCopySlug(blog.slug, e)}
                            title="Copy link to clipboard"
                            className="text-gray-400 hover:text-primary transition-colors p-0.5 rounded hover:bg-white dark:hover:bg-slate-700 shrink-0"
                          >
                            {copiedSlug === blog.slug ? (
                              <FiCheck className="w-3.5 h-3.5 text-emerald-500" />
                            ) : (
                              <FiCopy className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      )}

                      {/* Title */}
                      <h3
                        onClick={() => handleView(blog)}
                        className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-2 line-clamp-2 cursor-pointer hover:text-primary transition-colors leading-snug"
                        title={blog.title}
                      >
                        {blog.title}
                      </h3>

                      {/* Formatted Excerpt Preview */}
                      {renderBlogCardContent(blog)}

                      {/* Keywords / Tags Chips */}
                      {keywordsList.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-4">
                          {keywordsList.slice(0, 3).map((kw, idx) => (
                            <span
                              key={idx}
                              className="px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 border border-slate-200/60 dark:border-slate-700/60 hover:border-primary/40 transition-colors"
                            >
                              #{kw}
                            </span>
                          ))}
                          {keywordsList.length > 3 && (
                            <span className="text-[11px] font-medium text-gray-400 self-center px-1">
                              +{keywordsList.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="pt-3.5 border-t border-gray-100 dark:border-slate-800/80 flex items-center justify-between mt-auto">
                      <div className="flex items-center gap-2.5 min-w-0">
                        {authorName && (
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary/20 to-teal-500/20 text-primary flex items-center justify-center font-bold text-xs uppercase shrink-0 border border-primary/20 shadow-xs">
                            {authorName.charAt(0)}
                          </div>
                        )}
                        <div className="min-w-0">
                          {authorName && (
                            <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate leading-tight">
                              {authorName}
                            </p>
                          )}
                          <div className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-0.5">
                            {blog.publishedAt && (
                              <span className="flex items-center gap-1">
                                <FiCalendar className="w-3 h-3 text-gray-400" />
                                {formatDate(blog.publishedAt)}
                              </span>
                            )}
                            {blog.publishedAt && blog.views !== undefined && <span>•</span>}
                            {blog.views !== undefined && (
                              <span className="flex items-center gap-1" title={`${blog.views} views`}>
                                <FiEye className="w-3 h-3 text-gray-400" />
                                {blog.views}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleView(blog)}
                          className="p-2 text-gray-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-all duration-150"
                          title="Preview Live Blog"
                        >
                          <FiEye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(blog)}
                          className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all duration-150"
                          title={strings.EDIT_BLOG}
                        >
                          <FiEdit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(blog)}
                          className="p-2 text-gray-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-lg transition-all duration-150"
                          title={strings.DELETE_BLOG}
                        >
                          <GoTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* List View */
            <div className="space-y-4 mb-6">
              {blogs.map((blog) => {
                const blogId = blog._id || blog.id;
                const rawCover = blog.coverImage || blog.image;
                const coverImage = resolveImageUrl(rawCover);
                const authorName =
                  typeof blog.author === "string"
                    ? blog.author.trim()
                    : (blog.author?.name ? blog.author.name.trim() : "");

                const keywordsList = Array.isArray(blog.keywords)
                  ? blog.keywords
                  : (typeof blog.keywords === "string" && blog.keywords
                    ? blog.keywords.split(",").map((k) => k.trim()).filter(Boolean)
                    : (Array.isArray(blog.tags) ? blog.tags : []));

                return (
                  <div
                    key={blogId}
                    className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200/80 dark:border-slate-800 shadow-xs hover:shadow-lg hover:border-primary/40 transition-all duration-300 p-4 sm:p-5 flex flex-col md:flex-row gap-5 items-stretch"
                  >
                    {/* Thumbnail */}
                    <div className="relative w-full md:w-56 aspect-[16/9] md:aspect-auto rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800 shrink-0">
                      {coverImage ? (
                        <img
                          src={coverImage}
                          alt={blog.title}
                          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=800&auto=format&fit=crop&q=80";
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center p-4 bg-gradient-to-br from-teal-500/10 via-primary/10 to-indigo-500/15">
                          <FiBookOpen className="w-6 h-6 text-primary mb-1" />
                          {blog.category && (
                            <span className="text-[11px] font-semibold text-gray-500 uppercase">{blog.category}</span>
                          )}
                        </div>
                      )}
                      <div className="absolute top-2 left-2">
                        <button
                          type="button"
                          onClick={() => handleToggleStatus(blog)}
                          className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold backdrop-blur-md shadow-xs border flex items-center gap-1.5 ${getStatusBadgeClass(blog.status)}`}
                          title="Click to toggle status"
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${getStatusDotClass(blog.status)}`} />
                          <span>{blog.status || "Published"}</span>
                        </button>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 flex flex-col justify-between min-w-0">
                      <div>
                        <div className="flex flex-wrap items-center gap-2 mb-1.5">
                          {blog.category && (
                            <span className="px-2.5 py-0.5 rounded-md text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
                              {blog.category}
                            </span>
                          )}
                          {blog.slug && (
                            <span className="text-xs text-gray-400 font-mono flex items-center gap-1">
                              <FiGlobe className="w-3 h-3 text-primary" />
                              /blog/{blog.slug}
                            </span>
                          )}
                        </div>

                        <h3
                          onClick={() => handleView(blog)}
                          className="text-base sm:text-lg font-bold text-gray-900 dark:text-white hover:text-primary transition-colors cursor-pointer mb-2 line-clamp-1"
                        >
                          {blog.title}
                        </h3>

                        {renderBlogCardContent(blog)}

                        {keywordsList.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {keywordsList.slice(0, 4).map((kw, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-0.5 rounded text-[11px] font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
                              >
                                #{kw}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Footer */}
                      <div className="pt-3 border-t border-gray-100 dark:border-slate-800 flex items-center justify-between mt-2">
                        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                          {authorName && (
                            <span className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
                              <FiUser className="w-3.5 h-3.5 text-primary" />
                              {authorName}
                            </span>
                          )}
                          {authorName && blog.publishedAt && <span>•</span>}
                          {blog.publishedAt && (
                            <span className="flex items-center gap-1">
                              <FiCalendar className="w-3.5 h-3.5" />
                              {formatDate(blog.publishedAt)}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleView(blog)}
                            className="p-2 text-gray-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                            title="Preview Blog"
                          >
                            <FiEye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(blog)}
                            className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                            title={strings.EDIT_BLOG}
                          >
                            <FiEdit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(blog)}
                            className="p-2 text-gray-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-lg transition-colors"
                            title={strings.DELETE_BLOG}
                          >
                            <GoTrash className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalItems > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(newLimit) => {
                setItemsPerPage(newLimit);
                setCurrentPage(1);
              }}
              itemsPerPageOptions={[6, 10, 20, 50, 100]}
            />
          )}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-16 px-6 bg-white dark:bg-slate-900 rounded-xl border border-gray-200/80 dark:border-slate-800 text-center shadow-xs">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-4">
            <FiFileText className="w-7 h-7" />
          </div>
          <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-1.5">
            {searchQuery || statusFilter !== "All"
              ? "No matching articles found"
              : strings.NO_BLOGS_YET}
          </h3>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 max-w-md mb-6 leading-relaxed">
            {searchQuery || statusFilter !== "All"
              ? `We couldn't find any articles matching your search or filters${statusFilter !== "All" ? ` in "${statusFilter}"` : ""}. Try adjusting your criteria or clearing filters.`
              : "Start sharing insights and news with your community by creating your very first blog article."}
          </p>
          {searchQuery || statusFilter !== "All" ? (
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery("");
                setStatusFilter("All");
              }}
              className="text-xs sm:text-sm flex items-center h-10 px-4"
            >
              <FiX className="w-4 h-4 mr-1.5" />
              Clear All Filters
            </Button>
          ) : (
            <Button onClick={handleAdd} className="text-xs sm:text-sm flex items-center h-10 px-4">
              <IoMdAdd className="w-4 h-4 mr-1.5" />
              {strings.ADD_BLOG}
            </Button>
          )}
        </div>
      )}

      {/* Add / Edit Blog Modal */}
      <BlogModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedBlog(null);
        }}
        onSave={handleSave}
        blog={selectedBlog}
      />

      {/* Live Preview Modal */}
      <BlogPreviewModal
        isOpen={previewModal.isOpen}
        onClose={() => setPreviewModal({ isOpen: false, blog: null, loading: false })}
        onEdit={(blogToEdit) => {
          setPreviewModal({ isOpen: false, blog: null, loading: false });
          handleEdit(blogToEdit);
        }}
        blog={previewModal.blog}
        loading={previewModal.loading}
      />

      {/* Confirmation Modal for Delete */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, blog: null })}
        onConfirm={confirmDelete}
        title={strings.DELETE_BLOG_TITLE}
        message={confirmModal.blog ? strings.DELETE_BLOG_MESSAGE(confirmModal.blog.title) : ""}
        confirmText={strings.DELETE}
        variant="danger"
        icon={GoTrash}
      />
    </div>
  );
};

export default BlogsPage;
